import React from 'react'
import { connect } from 'react-redux'
import { graphql, Link } from 'gatsby'
import Helmet from 'react-helmet'
import citygateTheme from '../theme/'
import VehicleShowcase from '../components/VehicleShowcase'
import ContentVehicleMultiCardListing from '../components/ContentVehicleMultiCardListing'
import ContentSectionHeader from '../components/ContentSectionHeader'
import ContentFinanceTabs from '../components/ContentFinanceTabs'
import ContentFeatureList from '../components/ContentFeatureList'
import ContentVehicleOverview from '../components/ContentVehicleOverview'
import Breadcrumbs from '../components/Breadcrumbs'
import MobileBreadcrumb from '../components/MobileBreadcrumb'
import { contextSections } from '../setup/stubs'
import formatMileage from '../../lib/mileage-formatter'
import isRecent from '../../lib/recent-toggler'
import normaliseUsedVehicleForVehicleCard from '../../lib/cars/used/used-vehicle-to-card-normaliser'
import getModalPaths from '../../lib/modal-paths'
import createMeta from '../utils/meta-creator'
import Layout from '../components/Layout'
import { sortBy } from 'lodash'
import { setGlobalNumber } from '../state/phone-number/actions'
import LiveChat from '../components/LiveChat'
import moment from 'moment'
import Reviews from '../widgets/reviews'
import request from 'graphql-request'
import { isCar, findCategory } from '../../lib/category-matcher'
import { Container } from '../utils/style'
import ContentBar from '../components/ContentBar'
import { getVehicleData } from '../lib/vehicle-data-retriever'
import buildPath from '../../lib/cars/used/path-builder'
import {
  ContentTabsPanel,
  ContentTabsPanelItem
} from '../components/ContentTabsPanel'

const closestPriceFilter = (targetPrice, values, limit = 5) => {
  const sorted = sortBy(values, 'value')
  if (sorted.length <= limit) return sorted
  let startIndex = 0
  for (let i = 0; i < sorted.length; i += 1) {
    if (sorted[i].value > targetPrice) {
      startIndex = i - Math.floor(limit / 2)
      break
    }
  }
  const start = Math.max(0, Math.min(startIndex, sorted.length - limit))
  return sorted.slice(start, start + limit)
}

const inUrl = (path) => {
  if (typeof window === 'undefined') return false
  if (path instanceof RegExp) return path.test(window.location.pathname)
  return window.location.pathname.includes(path)
}

const getContactData = (dealership, saleType) => {
  if (dealership) {
    const {
      usedVehicleTelephoneNumber,
      usedVehicleTelephoneId,
      newVehicleTelephoneNumber,
      newVehicleTelephoneId,
      calltracksNumber,
      calltracksId
    } = dealership

    if (saleType === 'used' && usedVehicleTelephoneNumber) {
      return {
        number: usedVehicleTelephoneNumber,
        class: usedVehicleTelephoneId
      }
    }

    if (saleType === 'new' && newVehicleTelephoneNumber) {
      return {
        number: newVehicleTelephoneNumber,
        class: newVehicleTelephoneId
      }
    }

    if (calltracksNumber) {
      return {
        number: calltracksNumber,
        class: calltracksId
      }
    }
  }

  return {
    number: '0330 108 8020',
    class: 'calltracks_global-general'
  }
}

class UsedVehicleBase extends React.Component {
  constructor(props) {
    super(props)

    this.handleTabChange = this.handleTabChange.bind(this)

    this.resetFinance = (f) => f
    this.state = {
      financeTab: null,
      soldVehicle: {}
    }
  }

  componentDidMount() {
    const vehicle = this.props.data.vehicle
    const telephoneData = getContactData(vehicle.dealership, vehicle.saleType)

    this.updateDealershipNumber(telephoneData.number, telephoneData.class)
    this.updateSoldStatus().catch((e) => {
      console.error(e)
    })
  }

  componentWillUnmount() {
    // Reset to global phone number
    this.updateDealershipNumber()
  }

  async updateSoldStatus() {
    const query = `
    {
      soldVehicles(_id: "${this.props.data.vehicle.id}") {
        expiredDate
      }
    }`
    const { soldVehicles } = await request(
      `${process.env.GATSBY_API_URL}/graphql`,
      query
    )

    if (!soldVehicles.length) return

    this.setState({
      soldVehicle: {
        isSold: soldVehicles.length > 0,
        expiredDate: soldVehicles[0].expiredDate
      }
    })
  }

  updateDealershipNumber(phoneNumber, calltracksClass) {
    this.props.setGlobalNumber({ phoneNumber, calltracksClass })
  }

  handleTabChange(type) {
    this.setState(() => {
      return { financeTab: type }
    })
  }

  render() {
    const { data, location, pageContext, serverData } = this.props
    const { onOffer, category, offer } = pageContext
    let brandLocations = data.brandLocations.edges.map(({ node }) => node)
    let vehicleLocations =
      data.vehicleLocation && data.vehicleLocation.edges.map(({ node }) => node)
    let locations = brandLocations.concat(vehicleLocations)

    const vehicle = {
      ...data.vehicle,
      ...serverData.vehicle,
      ...this.state.soldVehicle
    }

    const preview = this.props.preview || serverData.previewId

    const usedVehicles =
      data.otherUsedVehicles &&
      closestPriceFilter(
        vehicle.value,
        data.otherUsedVehicles.edges.map(({ node }) => node)
      )

    const locationNames =
      locations.length > 0
        ? locations.map((location) => location.shortName)
        : [vehicle.dealership]

    const filteredContextSections = contextSections.filter(
      (section) => vehicle[section.slug] && vehicle[section.slug].length > 0
    )
    const metaTitle = `${vehicle.brand} ${vehicle.model} ${vehicle.registration} ${vehicle.dealership.shortName}`

    const isReserveEnquiryOpen = inUrl('/reserve')
    const isTestDriveOpen = inUrl('/book-a-test-drive')
    const isAskQuestionOpen = inUrl('/ask-a-question')
    const isExterior360Open = inUrl('/exterior-360')
    const isInterior360Open = inUrl('/interior-360')
    const isGalleryOpen = inUrl('/gallery')
    const vehicleAge = moment().year() - vehicle.year
    const showPcp =
      vehicleAge < 5 && vehicle.mileage <= 60000 && isCar(vehicle.category)
    let vehiclePaymentData

    if (!vehicle.isSold || !vehicle.expiredDate) {
      vehiclePaymentData = {
        total: vehicle.value,
        installment: {},
        reserve: vehicle.reservePrice
      }
      if (vehicle.pcpFinance && vehicle.pcpFinance.monthlyPayment) {
        vehiclePaymentData.installment.pcp = {
          payment: vehicle.pcpFinance.monthlyPayment,
          plan: 'Mth',
          code: 'MON'
        }
      }

      if (vehicle.hpFinance && vehicle.hpFinance.monthlyPayment) {
        vehiclePaymentData.installment.hp = {
          payment: vehicle.hpFinance.monthlyPayment,
          plan: 'Mth',
          code: 'MON'
        }
      }
    } else {
      vehiclePaymentData = { total: 'Sold', installment: {} }
    }

    let galleryIndex = 0
    if (inUrl(/\/gallery\/(\d+)/)) {
      ;[, galleryIndex] = window.location.pathname.match(/\/gallery\/(\d+)/)
    }

    const vehicleDealership = vehicle && vehicle.dealership
    const {
      reviewTitle,
      reviewEntities = [],
      reviewText,
      reviewCtaHref,
      reviewCtaLabel
    } = vehicleDealership

    const telephoneData = getContactData(vehicle.dealership, vehicle.saleType)
    const tabContent = filteredContextSections.slice(1)
    return (
      <Layout
        location={location}
        telephoneData={telephoneData}
        discoverable={!vehicle.expiredDate}
      >
        <Helmet
          title={metaTitle}
          meta={createMeta({
            ...vehicle,
            metaTitle,
            images: [vehicle.image]
          })}
          link={[
            {
              rel: 'canonical',
              href: `${process.env.GATSBY_SITE_URL}${buildPath(
                findCategory(vehicle.category).type,
                vehicle
              )}`
            }
          ]}
        />

        <div id="scroll-content-context">
          <div>
            <MobileBreadcrumb category={category} />
            <Breadcrumbs category={category} vehicle={vehicle} />
            {preview && (
              <ContentBar warning dismissable>
                This is a preview of the vehicle page ({vehicle.state})
              </ContentBar>
            )}
            <VehicleShowcase
              id={vehicle.id}
              key={`${vehicle.id}-showcase`}
              model={`${vehicle.brand} ${vehicle.model}`}
              engine={vehicle.derivative}
              category={vehicle.category}
              registration={vehicle.registration}
              vatIncluded={vehicle.vatCategory !== '+ VAT'}
              location={vehicle.dealership}
              details={[
                { label: 'Year', value: vehicle.year },
                { label: 'Mileage', value: formatMileage(vehicle.mileage) },
                { label: 'Colour', value: vehicle.colour },
                { label: 'Transmission', value: vehicle.transmission },
                { label: 'Fuel', value: vehicle.fuel }
              ]}
              price={vehiclePaymentData}
              locations={locationNames}
              path={vehicle.path}
              recent={isRecent(vehicle.publishedDate)}
              onOffer={onOffer}
              offer={offer}
              exteriorImages={vehicle.exteriorImages}
              panorama={vehicle.interiorImages[0]}
              reserved={!!vehicle.reservedDate}
              rawBrand={vehicle.brand}
              rawModel={vehicle.model}
              expiredDate={vehicle.expiredDate}
              isSold={vehicle.isSold || !!vehicle.expiredDate}
              isReserveEnquiryOpen={isReserveEnquiryOpen}
              isTestDriveOpen={isTestDriveOpen}
              isAskQuestionOpen={isAskQuestionOpen}
              isExterior360Open={isExterior360Open}
              isInterior360Open={isInterior360Open}
              isGalleryOpen={isGalleryOpen}
              galleryIndex={
                !isNaN(galleryIndex) ? parseInt(galleryIndex, 10) : 0
              }
              modalPaths={getModalPaths(vehicle)}
              images={[
                ...vehicle.images,
                ...((vehicle.brandApproved &&
                  vehicle.brandApproved.brandPromiseImages) ||
                  [])
              ]}
              onPaymentClick={(type) => {
                this.handleTabChange(type)
                this.resetFinance(type)
              }}
              hasLibraryImages={vehicle.hasKiaImages}
              video={vehicle.video}
              canPurchase={vehicle.canPurchase}
              reservable={vehicle.reservable}
            />
          </div>

          <ContentVehicleOverview
            sections={filteredContextSections}
            list={vehicle.features}
          />

          <Container px={20} my={[-50]}>
            <ContentTabsPanel
              items={tabContent.map((s) => ({
                label: s.label,
                value: s.slug
              }))}
            >
              {tabContent.map((section) => (
                <ContentTabsPanelItem
                  key={`content-accordion-${section.slug}`}
                  value={section.slug}
                >
                  <Container px={['3%']} py={[24, 32]}>
                    <ContentFeatureList
                      list={
                        section.pairs
                          ? vehicle[section.slug]
                              .filter((item) => item.value !== null)
                              .map(({ label, value }) =>
                                section.slug === 'technical' && label
                                  ? `${label}: ${value}`
                                  : value
                              )
                          : vehicle[section.slug]
                      }
                    />
                  </Container>
                </ContentTabsPanelItem>
              ))}
            </ContentTabsPanel>
          </Container>

          <ContentSectionHeader>
            <p>
              A selection of the vehicles advertised may be ex-fleet or multiple
              user. For terms and conditions see&nbsp;
              <Link to="/legal">https://citygate.co.uk/legal</Link>.
            </p>

            <p>
              The data displayed above is based on the usual specification of
              the most recent model of this vehicle. It is not the exact data
              for the actual vehicle being offered and data for older models may
              vary slightly so we recommend you check the vehicles details with
              a member of our team before you purchase.
            </p>
          </ContentSectionHeader>

          {!vehicle.isSold && (
            <ContentFinanceTabs
              showPcp={showPcp}
              activeTab={this.state.financeTab}
              onTabChange={this.handleTabChange}
              registration={vehicle.registration}
              category={vehicle.category}
              resetFn={(reset) => {
                this.resetFinance = reset
              }}
              phoneNumber={vehicle.dealership.calltracksNumber}
            />
          )}

          {usedVehicles && usedVehicles.length > 0 && (
            <section>
              <ContentSectionHeader
                title={`Other ${category}s you might like`}
                fg={citygateTheme.colors.blue}
              >
                <p>
                  Browse other {category}s, you might just find what you are
                  looking for…
                </p>
              </ContentSectionHeader>
              <ContentVehicleMultiCardListing
                vehicles={usedVehicles.map(normaliseUsedVehicleForVehicleCard)}
                fixedHeightImage={false}
              />
              <Reviews
                title={reviewTitle || 'Reviews'}
                reviewEntities={reviewEntities.map((e) => ({
                  apiId: e
                }))}
                description={reviewText}
                ctaHref={reviewCtaHref}
                ctaText={reviewCtaLabel}
              />
            </section>
          )}
        </div>
        {vehicle.dealership.liveChatProviderId && (
          <LiveChat providerId={vehicle.dealership.liveChatProviderId} />
        )}
      </Layout>
    )
  }
}

const mapDispatchToProps = (dispatch) => ({
  setGlobalNumber: (settings) => dispatch(setGlobalNumber(settings))
})

const UsedVehicle = connect(null, mapDispatchToProps)(UsedVehicleBase)

export default UsedVehicle

export const pageQuery = graphql`
  query usedVehicledItemQuery(
    $category: String!
    $registration: String!
    $lowerValue: Float
    $upperValue: Float
    $id: String!
    $brand: String
    $dealershipId: String
  ) {
    otherUsedVehicles: allUsedVehicleCatfish(
      limit: 10
      filter: {
        category: { eq: $category }
        expiredDate: { eq: null }
        state: { eq: "Published" }
        registration: { ne: $registration }
        value: { gt: $lowerValue, lt: $upperValue }
      }
    ) {
      edges {
        ...usedVehicleSummaryWidgetFields
      }
    }
    vehicleLocation: allDealershipCatfish(
      filter: { _id: { eq: $dealershipId } }
    ) {
      edges {
        node {
          slug
          shortName
          location
          vehicles {
            name
          }
        }
      }
    }
    brandLocations: allDealershipCatfish(
      filter: {
        disableVehicleAllocating: { ne: true }
        vehicles: { elemMatch: { slug: { eq: $brand } } }
      }
      sort: { shortName: ASC }
    ) {
      edges {
        node {
          slug
          shortName
          location
          vehicles {
            name
          }
        }
      }
    }
    vehicle: usedVehicleCatfish(_id: { eq: $id }) {
      _id
      id: _id
      brand
      model
      category
      derivative
      registration
      colour
      mileage
      value
      reservable
      reservePrice
      dealership {
        slug
        shortName
        location
        liveChatProviderId
        calltracksNumber
        calltracksId
        newVehicleTelephoneNumber
        newVehicleTelephoneId
        usedVehicleTelephoneNumber
        usedVehicleTelephoneId
        reviewTitle
        reviewEntities
        reviewText
        reviewCtaHref
        reviewCtaLabel
      }
      transmission
      transmissionStyle
      fuel
      year
      image
      images
      bodyStyle
      featured
      seats
      mpg
      co2
      bhp
      features
      interior {
        label
        value
      }
      exterior {
        label
        value
      }
      hpFinance {
        monthlyPayment
        carPrice
        depositAmount
        adminFee
        balanceToFinance
        firstPayment
        numberOfPayments
        finalPayment
        amountPayable
        interestCharges
        interestApr
        interestRate
        optionFee
        lender
      }
      pcpFinance {
        monthlyPayment
        carPrice
        depositAmount
        adminFee
        balanceToFinance
        firstPayment
        numberOfPayments
        finalPayment
        amountPayable
        interestCharges
        interestApr
        interestRate
        optionFee
        lender
      }
      reservedDate
      vatCategory
      expiredDate
      publishedDate
      exteriorImages
      interiorImages
      technical {
        label
        value
      }
      safety {
        label
        value
      }
      state
      hasKiaImages
      isSold
      taxInfo {
        price
      }
      video
      saleType
      brandApproved {
        brandPromiseImages
      }
    }
  }
`

export async function getServerData(context) {
  const result = await getVehicleData(context)

  if (
    result.props.vehicle &&
    result.props.vehicle.lastModifiedDate &&
    (context.pageContext.lastModifiedDate !==
      result.props.vehicle.lastModifiedDate ||
      result.props.vehicle.previewId)
  ) {
    return result
  }

  return {
    props: {},
    headers: {
      'cache-control': 'max-age=3600, public'
    }
  }
}
